import React, { useState } from "react";
import { Link } from "react-router-dom";
import SolutionsDropdown from "./solutions-dropdown";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react";

const solutions = [
  {
    name: "Dispatch Planning",
    path: "/dispatch-planning",
  },
  {
    name: "Contract and Spot Auction",
    path: "/freight-sourcing-and-reverse-auction",
  },
  {
    name: "Indent Automation",
    path: "/indent-automation",
  },
  {
    name: "In-Plant Vehicle Movement and Optimization",
    path: "/in-plant-vehicle-movement-and-optimization",
  },
  {
    name: "Tracking",
    path: "/tracking",
  },
  {
    name: "EXIM (Import/Export)",
    path: "/exim",
  },
  {
    name: "Proof of Delivery",
    path: "/proof-of-delivery",
  },
  {
    name: "Invoicing",
    path: "/invoicing",
  },
  {
    name: "Dashboard and Analytics",
    path: "/dashboard-analytics",
  },
];

const Navbar = () => {
  const [showSolutions, setShowSolutions] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleDropdown = (menu) => {
    if (menu === "solutions") {
      setShowSolutions(!showSolutions);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setShowSolutions(false);
  };

  return (
    <nav className="bg-[#f0f3fc] px-4 md:px-16 py-4 relative z-50 shadow-sm">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center space-x-2"
          onClick={closeMobileMenu}
        >
          <img
            src="/images/CargoProLogo.webp"
            alt="CargoPro Logo"
            loading="lazy"
            decoding="async"
            width="56"
            height="56"
            className="h-8 w-auto"
          />
          <div className="font-raleway font-semibold text-[24px] leading-[24px] text-primaryBlue">
            CargoPro
          </div>
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex space-x-6 text-[16px] font-medium text-primaryBlue relative">
          <li className="relative">
            <button
              onClick={() => toggleDropdown("solutions")}
              className="flex items-center space-x-1 text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              <span>Solutions</span>
              {showSolutions ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
            {showSolutions && (
              <SolutionsDropdown closeMobileMenu={closeMobileMenu} />
            )}
          </li>

          <li>
            <Link
              to="/features"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              Features
            </Link>
          </li>

          <li>
            <Link
              to="/industries"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              Industries
            </Link>
          </li>

          <li>
            <Link
              to="/blogs"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              Blog
            </Link>
          </li>

          <li>
            <Link
              to="/about"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              About
            </Link>
          </li>

          <li>
            <Link
              to="/careers"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              Careers
            </Link>
          </li>

          <li>
            <Link
              to="/careers#contact-us"
              className="text-primaryBlue/90 hover:text-primaryBlue transition-colors duration-200 relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:scale-x-0 after:origin-left after:bg-primaryBlue after:transition-transform after:duration-200 hover:after:scale-x-100"
            >
              Contacts
            </Link>
          </li>
        </ul>

        {/* Desktop CTA */}
        <div className="hidden md:flex space-x-4">
          <a
            href="https://calendly.com/pranav-cargopro/30min"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="bg-blue-700 text-white px-4 py-2 rounded-full transition-colors duration-200 inline-block shadow-sm hover:shadow-lg hover:bg-blue-800 transform hover:-translate-y-0.5"
          >
            Book a Demo
          </a>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button
            aria-label={
              isMobileMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-primaryBlue" />
            ) : (
              <Menu className="h-6 w-6 text-primaryBlue" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 px-4 py-6 bg-[#f0f3fc] rounded-md shadow-lg space-y-3 text-primaryBlue text-[16px] font-medium">
          {/* Solutions dropdown toggle button */}
          <button
            onClick={() => toggleDropdown("solutions")}
            className="w-full flex justify-between items-center py-2"
          >
            <span>Solutions</span>
            {showSolutions ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
          {/* Show solutions links when toggled */}
          {showSolutions && (
            <div className="space-y-2 pl-4">
              {solutions.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className="block hover:text-blue-700"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          <a href="/features" className="block hover:text-primaryBlue">
            Features
          </a>

          <a href="/industries" className="block hover:text-primaryBlue">
            Industries
          </a>

          <a href="/blogs" className="block hover:text-primaryBlue">
            Blog
          </a>

          <Link
            to="/about"
            onClick={closeMobileMenu}
            className="block hover:text-primaryBlue"
          >
            About
          </Link>

          <Link
            to="/careers"
            onClick={closeMobileMenu}
            className="block hover:text-primaryBlue"
          >
            Careers
          </Link>

          <a href="/contacts" className="block hover:text-primaryBlue">
            Contact
          </a>

          <hr className="my-3" />
          <a
            href="https://calendly.com/pranav-cargopro/30min"
            rel="noopener noreferrer nofollow"
            className="block text-center w-full bg-blue-700 text-white rounded-full py-2 hover:bg-blue-800 transition"
          >
            Book a Demo
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
